import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Save, Send, ChevronDown, ChevronRight, Plus, 
  Loader2, Eye, Clock, Check, GitBranch, Sparkles, Settings,
  MoreVertical, Edit, Trash2, RefreshCw, GripVertical, RotateCcw
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSafeMode } from '../contexts/SafeModeContext';
import { cn } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import TimelineRenderer from '../components/visualizations/TimelineRenderer';
import UnifiedCoPilot from '../components/copilot/UnifiedCoPilot';
import VisualizationSettingsModal from '../components/visualizations/VisualizationSettingsModal';
import ReviewAccessModal from '../components/visualizations/ReviewAccessModal';

const API = process.env.REACT_APP_BACKEND_URL;

// Default and min/max widths for co-pilot panel
const COPILOT_DEFAULT_WIDTH = 420; // ~30% wider than 320
const COPILOT_MIN_WIDTH = 300;
const COPILOT_MAX_WIDTH = 600;

const VisualizationWorkspace = () => {
  const { visualizationId } = useParams();
  const navigate = useNavigate();
  const { token, organization } = useAuth();
  const { getThemeColors } = useSafeMode();
  const colors = getThemeColors();
  
  // State
  const [visualization, setVisualization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [activeVersionId, setActiveVersionId] = useState(null);
  const [timelineItems, setTimelineItems] = useState([]);
  const [showCopilot, setShowCopilot] = useState(true);
  const [showVersions, setShowVersions] = useState(true);
  const [copilotWidth, setCopilotWidth] = useState(COPILOT_DEFAULT_WIDTH);
  const [showSettings, setShowSettings] = useState(false);
  const [showReviewAccess, setShowReviewAccess] = useState(false);
  const [copilotConfig, setCopilotConfig] = useState(null);
  
  // Resize handling
  const isResizing = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);
  
  // Auto-save timer
  const autoSaveTimer = useRef(null);
  const lastSavedItems = useRef(null);
  
  // Load co-pilot configuration from database
  useEffect(() => {
    const loadCopilotConfig = async () => {
      try {
        const response = await fetch(
          `${API}/api/admin/ai-prompts/by-agent-type/visualization_copilot`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setCopilotConfig(data);
        }
      } catch (error) {
        console.error('Error loading copilot config:', error);
      }
    };
    loadCopilotConfig();
  }, [token]);
  
  // Build injectedContext with current timeline data (full tree, including children)
  const injectedContext = useMemo(() => {
    if (!timelineItems || timelineItems.length === 0) {
      return `=== CURRENT TIMELINE ===
Total Items: 0
Timeline is empty - starting fresh.
=== END CURRENT TIMELINE ===`;
    }
    
    const formatItem = (item) => {
      const formatted = {
        item_id: item.item_id,
        level: item.level,
        title: item.title,
        description: item.description || '',
        date_display: item.date_display || null,
        badge: item.badge || null,
        source: item.source || null,
      };
      if (item.children?.length > 0) {
        formatted.children = item.children.map(formatItem);
      }
      return formatted;
    };
    
    const formattedItems = timelineItems.map(formatItem);
    
    return `=== CURRENT TIMELINE ===
Total Items: ${timelineItems.length}

${JSON.stringify(formattedItems, null, 2)}
=== END CURRENT TIMELINE ===`;
  }, [timelineItems]);
  
  // Get quick actions based on current audience (resolved here, passed to UnifiedCoPilot)
  const quickActions = useMemo(() => {
    const quickActionsConfig = copilotConfig?.copilot_settings?.quick_actions || {};
    const audience = visualization?.target_audience?.toLowerCase() || 'team';
    
    // Map audience to config key
    let audienceKey = 'team';
    if (audience === 'board' || audience === 'executive') {
      audienceKey = 'board';
    } else if (audience === 'customer' || audience === 'external' || audience === 'public') {
      audienceKey = 'customer';
    } else if (audience === 'custom') {
      audienceKey = 'custom';
    }
    
    // Combine general + audience-specific actions
    const generalActions = (quickActionsConfig.general || []).filter(a => a.is_enabled !== false);
    const audienceActions = (quickActionsConfig[audienceKey] || []).filter(a => a.is_enabled !== false);
    
    return [...generalActions, ...audienceActions];
  }, [copilotConfig, visualization?.target_audience]);
  
  // Handle AI response - extract timeline_items and apply to canvas
  const handleResponseReceived = useCallback((response) => {
    if (response.timeline_items && response.timeline_items.length > 0) {
      handleTimelineUpdate(response.timeline_items);
      toast.success(`Timeline updated with ${response.timeline_items.length} items`);
      return { handled: true }; // Skip action buttons
    }
    return { handled: false }; // Let default action processing continue
  }, []);
  
  // Handle settings save
  const handleSettingsSave = (updatedVisualization) => {
    setVisualization(updatedVisualization);
  };
  
  // Handle resize start
  const handleResizeStart = (e) => {
    isResizing.current = true;
    startX.current = e.clientX;
    startWidth.current = copilotWidth;
    document.addEventListener('mousemove', handleResizeMove);
    document.addEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };
  
  // Handle resize move
  const handleResizeMove = (e) => {
    if (!isResizing.current) return;
    const diff = startX.current - e.clientX;
    const newWidth = Math.min(
      Math.max(startWidth.current + diff, COPILOT_MIN_WIDTH),
      COPILOT_MAX_WIDTH
    );
    setCopilotWidth(newWidth);
  };
  
  // Handle resize end
  const handleResizeEnd = () => {
    isResizing.current = false;
    document.removeEventListener('mousemove', handleResizeMove);
    document.removeEventListener('mouseup', handleResizeEnd);
    document.body.style.cursor = '';
    document.body.style.userSelect = '';
  };
  
  // Fetch visualization
  const fetchVisualization = useCallback(async () => {
    try {
      const response = await fetch(`${API}/api/visualizations/${visualizationId}`, {
        credentials: 'include',
      });
      
      if (response.ok) {
        const data = await response.json();
        setVisualization(data);
        
        // Set active version (working draft or latest)
        const workingDraft = data.working_draft_id;
        const activeVersion = workingDraft 
          ? data.versions.find(v => v.version_id === workingDraft)
          : data.versions[data.versions.length - 1];
        
        if (activeVersion) {
          setActiveVersionId(activeVersion.version_id);
          setTimelineItems(activeVersion.timeline_items || []);
          lastSavedItems.current = JSON.stringify(activeVersion.timeline_items || []);
        }
      } else {
        toast.error('Failed to load visualization');
        navigate('/visualizations');
      }
    } catch (error) {
      console.error('Error fetching visualization:', error);
      toast.error('Failed to load visualization');
      navigate('/visualizations');
    } finally {
      setLoading(false);
    }
  }, [visualizationId, token, navigate]);
  
  useEffect(() => {
    fetchVisualization();
  }, [fetchVisualization]);
  
  // Track changes
  useEffect(() => {
    if (lastSavedItems.current !== null) {
      const currentItems = JSON.stringify(timelineItems);
      setHasUnsavedChanges(currentItems !== lastSavedItems.current);
    }
  }, [timelineItems]);
  
  // Auto-save
  useEffect(() => {
    if (hasUnsavedChanges && activeVersionId) {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
      
      autoSaveTimer.current = setTimeout(() => {
        handleSave(true); // silent save
      }, 30000); // 30 seconds
    }
    
    return () => {
      if (autoSaveTimer.current) {
        clearTimeout(autoSaveTimer.current);
      }
    };
  }, [hasUnsavedChanges, timelineItems, activeVersionId]);
  
  // Save version
  const handleSave = async (silent = false) => {
    if (!activeVersionId) return;
    
    setSaving(true);
    
    try {
      const response = await fetch(
        `${API}/api/visualizations/${visualizationId}/versions/${activeVersionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ timeline_items: timelineItems }),
        }
      );
      
      if (response.ok) {
        lastSavedItems.current = JSON.stringify(timelineItems);
        setHasUnsavedChanges(false);
        if (!silent) {
          toast.success('Saved successfully');
        }
      } else {
        toast.error('Failed to save');
      }
    } catch (error) {
      console.error('Error saving:', error);
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };
  
  // Publish version
  const handlePublish = async () => {
    if (!activeVersionId) return;
    
    // Save first
    await handleSave(true);
    
    setSaving(true);
    
    try {
      const response = await fetch(
        `${API}/api/visualizations/${visualizationId}/versions/${activeVersionId}`,
        {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ status: 'published' }),
        }
      );
      
      if (response.ok) {
        toast.success('Published successfully!');
        setShowReviewAccess(false);
        fetchVisualization(); // Refresh to get updated version statuses
      } else {
        toast.error('Failed to publish');
      }
    } catch (error) {
      console.error('Error publishing:', error);
      toast.error('Failed to publish');
    } finally {
      setSaving(false);
    }
  };
  
  // Unpublish — return published version to draft
  const handleUnpublish = async () => {
    if (!activeVersionId) return;
    
    setSaving(true);
    
    try {
      const response = await fetch(
        `${API}/api/visualizations/${visualizationId}/versions/${activeVersionId}`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ status: 'draft' }),
        }
      );
      
      if (response.ok) {
        toast.success('Returned to draft');
        fetchVisualization();
      } else {
        toast.error('Failed to return to draft');
      }
    } catch (error) {
      console.error('Error unpublishing:', error);
      toast.error('Failed to return to draft');
    } finally {
      setSaving(false);
    }
  };
  
  // Create new version
  const handleCreateVersion = async (basedOnVersionId = null) => {
    try {
      const response = await fetch(
        `${API}/api/visualizations/${visualizationId}/versions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
          body: JSON.stringify({ based_on_version_id: basedOnVersionId }),
        }
      );
      
      if (response.ok) {
        const newVersion = await response.json();
        toast.success('New draft created');
        fetchVisualization();
        setActiveVersionId(newVersion.version_id);
        setTimelineItems(newVersion.timeline_items || []);
        lastSavedItems.current = JSON.stringify(newVersion.timeline_items || []);
        setHasUnsavedChanges(false);
      } else {
        toast.error('Failed to create new version');
      }
    } catch (error) {
      console.error('Error creating version:', error);
      toast.error('Failed to create new version');
    }
  };
  
  // Switch version
  const handleSwitchVersion = (version) => {
    // Warn if unsaved changes
    if (hasUnsavedChanges) {
      const confirm = window.confirm('You have unsaved changes. Discard them?');
      if (!confirm) return;
    }
    
    setActiveVersionId(version.version_id);
    setTimelineItems(version.timeline_items || []);
    lastSavedItems.current = JSON.stringify(version.timeline_items || []);
    setHasUnsavedChanges(false);
  };
  
  // Handle timeline updates from co-pilot or manual edits
  const handleTimelineUpdate = (newItems) => {
    setTimelineItems(newItems);
  };
  
  // Get active version details
  const activeVersion = visualization?.versions?.find(v => v.version_id === activeVersionId);
  const isPublished = activeVersion?.status === 'published';
  const isArchived = activeVersion?.status === 'archived';
  const canEdit = activeVersion?.status === 'draft';
  
  if (loading) {
    return (
      <div className={cn("h-screen flex items-center justify-center", colors.bgPrimary)}>
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }
  
  if (!visualization) {
    return null;
  }
  
  return (
    <div className={cn("h-screen flex flex-col", colors.bgPrimary)}>
      {/* Header */}
      <div className={cn("border-b px-4 py-3 flex items-center justify-between", colors.borderPrimary)}>
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/visualizations')}
            className="gap-1"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </Button>
          
          <div>
            <h1 className={cn("font-semibold", colors.textPrimary)}>
              {visualization.name}
            </h1>
            <p className={cn("text-xs", colors.textMuted)}>
              {visualization.product_names?.join(', ')}
            </p>
          </div>
          
          {/* Unsaved indicator */}
          {hasUnsavedChanges && (
            <span className="text-xs text-amber-600 dark:text-amber-400 flex items-center gap-1">
              <span className="w-2 h-2 rounded-full bg-amber-500 animate-pulse" />
              Unsaved changes
            </span>
          )}
        </div>
        
        <div className="flex items-center gap-2">
          {/* Settings Button - Always available */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowSettings(true)}
            className="gap-1"
            data-testid="settings-btn"
          >
            <Settings className="w-4 h-4" />
            Settings
          </Button>
          
          {/* Save Button */}
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleSave()}
            disabled={saving || !hasUnsavedChanges || !canEdit}
            className="gap-1"
            data-testid="save-btn"
          >
            {saving ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Save className="w-4 h-4" />
            )}
            Save Draft
          </Button>
          
          {/* Publish / Unpublish Button */}
          {isPublished ? (
            <Button
              size="sm"
              onClick={handleUnpublish}
              disabled={saving}
              className="gap-1 bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="unpublish-btn"
            >
              <RotateCcw className="w-4 h-4" />
              Return to Draft
            </Button>
          ) : (
            <Button
              size="sm"
              onClick={() => setShowReviewAccess(true)}
              disabled={saving || !canEdit}
              className="gap-1 bg-green-600 hover:bg-green-700"
              data-testid="publish-btn"
            >
              <Send className="w-4 h-4" />
              Publish
            </Button>
          )}
          
          {/* View Published */}
          {visualization.published_version_id && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(`/viz/${visualization.share_token}`, '_blank')}
              className="gap-1"
            >
              <Eye className="w-4 h-4" />
              View Live
            </Button>
          )}
        </div>
      </div>
      
      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {/* Left Sidebar - Versions */}
        <div className={cn(
          "border-r flex flex-col transition-all duration-300",
          colors.borderPrimary,
          colors.bgSecondary,
          showVersions ? "w-64" : "w-0"
        )}>
          {showVersions && (
            <>
              <div className="p-3 border-b flex items-center justify-between" style={{ borderColor: 'var(--border)' }}>
                <h3 className={cn("font-medium text-sm", colors.textPrimary)}>
                  Versions
                </h3>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={() => handleCreateVersion(activeVersionId)}
                  title="Create new draft from current"
                >
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              
              <div className="flex-1 overflow-y-auto p-2 space-y-1">
                {[...visualization.versions].reverse().map(version => {
                  const isActive = version.version_id === activeVersionId;
                  const statusColor = {
                    draft: 'text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30',
                    published: 'text-green-600 bg-green-100 dark:bg-green-900/30',
                    archived: 'text-gray-500 bg-gray-100 dark:bg-gray-800',
                  }[version.status];
                  
                  return (
                    <button
                      key={version.version_id}
                      onClick={() => handleSwitchVersion(version)}
                      className={cn(
                        "w-full p-3 rounded-lg text-left transition-all",
                        isActive
                          ? "bg-purple-100 dark:bg-purple-900/30 border border-purple-500"
                          : cn(colors.bgPrimary, "hover:bg-gray-100 dark:hover:bg-gray-800 border border-transparent")
                      )}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className={cn("font-medium text-sm", colors.textPrimary)}>
                          v{version.version_number}
                        </span>
                        <span className={cn("text-xs px-1.5 py-0.5 rounded", statusColor)}>
                          {version.status}
                        </span>
                      </div>
                      <div className={cn("text-xs", colors.textMuted)}>
                        <div className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {new Date(version.created_at).toLocaleDateString()}
                        </div>
                        <div className="truncate mt-0.5">
                          {version.created_by_name}
                        </div>
                      </div>
                      {version.based_on_version && (
                        <div className={cn("text-xs mt-1 flex items-center gap-1", colors.textMuted)}>
                          <GitBranch className="w-3 h-3" />
                          from v{visualization.versions.find(v => v.version_id === version.based_on_version)?.version_number}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </>
          )}
        </div>
        
        {/* Toggle Versions Panel */}
        <button
          onClick={() => setShowVersions(prev => !prev)}
          className={cn(
            "absolute left-0 top-1/2 -translate-y-1/2 z-10 p-1 rounded-r border border-l-0",
            colors.bgSecondary,
            colors.borderPrimary,
            showVersions ? "ml-64" : "ml-0"
          )}
          style={{ transition: 'margin-left 0.3s' }}
        >
          {showVersions ? (
            <ChevronDown className={cn("w-4 h-4 rotate-90", colors.textMuted)} />
          ) : (
            <ChevronRight className={cn("w-4 h-4", colors.textMuted)} />
          )}
        </button>
        
        {/* Center - Timeline Preview */}
        <div className="flex-1 overflow-hidden flex flex-col">
          {/* Read-only notice */}
          {!canEdit && (
            <div className={cn(
              "px-4 py-2 text-sm flex items-center justify-between",
              isPublished 
                ? "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400"
                : "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400"
            )}>
              <span>
                {isPublished 
                  ? "This version is published. Create a new draft to make changes."
                  : "This version is archived. Create a new draft to make changes."
                }
              </span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleCreateVersion(activeVersionId)}
                className="gap-1"
              >
                <GitBranch className="w-3 h-3" />
                Create Draft
              </Button>
            </div>
          )}
          
          {/* Timeline Preview Area */}
          <div className="flex-1 overflow-y-auto p-6">
            <TimelineRenderer
              items={timelineItems}
              config={visualization.timeline_config}
              branding={visualization.branding_template?.branding || {}}
              brandingOverrides={{ ...visualization.branding_overrides, logo_url: organization?.logo_url || visualization.branding_overrides?.logo_url || '' }}
              editable={canEdit}
              onUpdate={handleTimelineUpdate}
            />
          </div>
        </div>
        
        {/* Right Sidebar - AI Co-pilot (Resizable) */}
        <div 
          className={cn(
            "border-l flex flex-col relative",
            colors.borderPrimary,
            !showCopilot && "w-0 overflow-hidden"
          )}
          style={{ 
            width: showCopilot ? copilotWidth : 0,
            transition: showCopilot ? 'none' : 'width 0.3s'
          }}
        >
          {/* Resize Handle */}
          {showCopilot && (
            <div
              className={cn(
                "absolute left-0 top-0 bottom-0 w-1 cursor-col-resize group",
                "hover:bg-purple-500/50 active:bg-purple-500"
              )}
              onMouseDown={handleResizeStart}
            >
              <div className={cn(
                "absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2",
                "opacity-0 group-hover:opacity-100 transition-opacity"
              )}>
                <GripVertical className="w-3 h-3 text-gray-400" />
              </div>
            </div>
          )}
          
          {showCopilot && (
            <UnifiedCoPilot
              contextType="visualization"
              contextId={visualizationId}
              productId={visualization?.product_ids?.[0]}
              contextData={visualization}
              quickActions={quickActions}
              injectedContext={injectedContext}
              onResponseReceived={handleResponseReceived}
              onApplyUpdates={() => {}} // No-op - visualization uses onResponseReceived instead
              onRefresh={fetchVisualization}
              variant="workflow"
              className="h-full"
            />
          )}
        </div>
        
        {/* Toggle Copilot Panel */}
        <button
          onClick={() => setShowCopilot(prev => !prev)}
          className={cn(
            "absolute right-0 top-1/2 -translate-y-1/2 z-10 p-1 rounded-l border border-r-0",
            colors.bgSecondary,
            colors.borderPrimary
          )}
          style={{ 
            marginRight: showCopilot ? copilotWidth : 0,
            transition: 'margin-right 0.3s'
          }}
        >
          {showCopilot ? (
            <ChevronDown className={cn("w-4 h-4 -rotate-90", colors.textMuted)} />
          ) : (
            <ChevronDown className={cn("w-4 h-4 rotate-90", colors.textMuted)} />
          )}
        </button>
      </div>
      
      {/* Settings Modal */}
      {showSettings && (
        <VisualizationSettingsModal
          visualization={visualization}
          onClose={() => setShowSettings(false)}
          onSave={handleSettingsSave}
          canEditContent={canEdit}
        />
      )}
      
      {showReviewAccess && (
        <ReviewAccessModal
          visualization={visualization}
          onClose={() => setShowReviewAccess(false)}
          onPublish={handlePublish}
        />
      )}
    </div>
  );
};

export default VisualizationWorkspace;
